import React, { useEffect, useState } from 'react';
import styles from './ManageStartupPage.module.css';
import MainLayout from '../../layouts/MainLayout';
import apiClient from '../../api/apiClient';
import ManageStartupPageCreateStartupPopup from './ManageStartupPageCreateStartupPopup';
import EditParticipantPopup from './ManageStartupPageEditParticipantPopup';
import Banner from '../../components/Banner';
import ConfirmDialog from '../../components/Dialog';

const ManageStartupPage = () => {
  const client = apiClient('survey');
  const [participants, setParticipants] = useState([]);
  const [filteredParticipants, setFilteredParticipants] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDetailPopupOpen, setIsDetailPopupOpen] = useState(false);
  const [editableParticipant, setEditableParticipant] = useState(null);
  const [isCreatePopupOpen, setIsCreatePopupOpen] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    identifier: '',
  });
  const [banner, setBanner] = useState({
    open: false,
    message: '',
    severity: 'info',
  });

  const openParticipantDetail = (startupEntry, participantKey) => {
    setEditableParticipant({
      ...startupEntry,
      editingParticipantKey: participantKey,
    });
    setIsDetailPopupOpen(true);
  };

  const updateField = (field, value) => {
    setEditableParticipant((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const updateListField = (field, index, value) => {
    const key = editableParticipant.editingParticipantKey;
    const updatedList = [...(editableParticipant[key]?.[field] || [])];
    updatedList[index] = value;
    setEditableParticipant((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: updatedList,
      },
    }));
  };

  const removeListItem = (field, index) => {
    const key = editableParticipant.editingParticipantKey;
    const updatedList = [...(editableParticipant[key]?.[field] || [])];
    updatedList.splice(index, 1);
    setEditableParticipant((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: updatedList,
      },
    }));
  };

  const addListItem = (field) => {
    const key = editableParticipant.editingParticipantKey; // 'e' or 'r'
    setEditableParticipant((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: [...(prev[key]?.[field] || []), ''],
      },
    }));
  };

  const handleSubmitChanges = async () => {
    try {
      await client.put(
        `/participant/by-identifier/${editableParticipant.identifier}`,
        editableParticipant
      );
      setIsDetailPopupOpen(false);
      fetchParticipants();
    } catch (error) {
      console.error('Failed to update participant:', error);
      setBanner({
        open: true,
        message:
          error.response && error.response.statusText
            ? error.response.statusText
            : 'Update failed. Please try again.',
        severity: 'error',
      });
    }
  };

  const handleStartupListUpdate = (index, newValue) => {
    const updated = [...editableParticipant.startup_name];
    updated[index] = newValue;
    setEditableParticipant({ ...editableParticipant, startup_name: updated });
  };

  const addStartupListItem = () => {
    setEditableParticipant({
      ...editableParticipant,
      startup_name: [...(editableParticipant.startup_name || []), ''],
    });
  };

  const removeStartupListItem = (index) => {
    const updated = [...editableParticipant.startup_name];
    updated.splice(index, 1);
    setEditableParticipant({ ...editableParticipant, startup_name: updated });
  };

  const fetchParticipants = async () => {
    try {
      const response = await client.get('/participant/all');
      setParticipants(response.data);
      setFilteredParticipants(response.data); // initial filter
      if (Array.isArray(response.data) && response.data.length === 0) {
        setBanner({
          open: true,
          message:
            'No startups found. In case the survey service was restarted lately, please logout (by clicking on your username on the top right corner and selecting: Logout), then login and try again.',
          severity: 'error',
        });
      }
    } catch (error) {
      console.error('Failed to fetch participants:', error);
      setBanner({
        open: true,
        message:
          error.response && error.response.statusText
            ? error.response.statusText
            : 'Failed to load participants.',
        severity: 'error',
      });
    }
  };

  useEffect(() => {
    fetchParticipants();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const term = searchTerm.toLowerCase();
    const filtered = participants.filter((entry) => {
      const id = entry?.identifier ?? '';
      // Check all startup names
      const startupNames = entry?.startup_name ?? [];
      // Check all full names and codes for both 'e' and 'r'
      const fullNamesE = entry?.e?.full_name ?? [];
      const codesE = entry?.e?.code ?? [];
      const fullNamesR = entry?.r?.full_name ?? [];
      const codesR = entry?.r?.code ?? [];

      // Helper to check if any item in a list matches the term
      const matchesList = (list) =>
        Array.isArray(list) &&
        list.some((item) => (item ?? '').toLowerCase().includes(term));

      return (
        id.toLowerCase().includes(term) ||
        matchesList(startupNames) ||
        matchesList(fullNamesE) ||
        matchesList(codesE) ||
        matchesList(fullNamesR) ||
        matchesList(codesR)
      );
    });
    setFilteredParticipants(filtered);
  }, [searchTerm, participants]);

  // Handler for delete button in popup
  const handleDeleteIdentifier = () => {
    setDeleteDialog({ open: true, identifier: editableParticipant.identifier });
  };

  // Confirm delete logic
  const confirmDeleteIdentifier = async () => {
    try {
      await client.delete(
        `/surveys/participant/by-identifier/${deleteDialog.identifier}`
      );
      setBanner({
        open: true,
        message: 'Startup deleted successfully.',
        severity: 'success',
      });
      setIsDetailPopupOpen(false);
      setDeleteDialog({ open: false, identifier: '' });
      fetchParticipants();
    } catch (error) {
      let msg = 'Failed to delete Startup.';
      if (error.response && error.response.data && error.response.data.detail) {
        msg = error.response.data.detail;
      }
      setBanner({
        open: true,
        message: msg,
        severity: 'error',
      });
      setDeleteDialog({ open: false, identifier: '' });
    }
  };

  const cancelDeleteIdentifier = () => {
    setDeleteDialog({ open: false, identifier: '' });
  };

  return (
    <MainLayout>
      <div className={styles.container}>
        <h2 className={styles.title}>Manage Startups</h2>

        <div>
          <button
            className={styles.addButton}
            style={{ marginBottom: '1rem' }}
            onClick={() => {
              setIsCreatePopupOpen(true);
            }}
          >
            + Create Startup
          </button>
        </div>
        <div className={styles.topControls}>
          <input
            className={styles.searchInput}
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <table className={styles.table}>
          <thead>
            <tr>
              <th>Identifier</th>
              <th>Startup Name</th>
              <th>Full Name E</th>
              <th>Full Name R</th>
            </tr>
          </thead>
          <tbody>
            {filteredParticipants.map((entry, index) => {
              const identifier = entry?.identifier ?? '—';
              const startupName = entry?.startup_name?.[0] ?? '—';
              const fullNameE = entry?.e?.full_name?.[0] ?? '—';
              const fullNameR = entry?.r?.full_name?.[0] ?? '—';
              return (
                <tr key={index}>
                  <td>{identifier}</td>
                  <td>{startupName}</td>
                  <td>
                    <span
                      className={styles.clickable}
                      onClick={() => openParticipantDetail(entry, 'e')}
                    >
                      {fullNameE}
                    </span>
                  </td>
                  <td>
                    <span
                      className={styles.clickable}
                      onClick={() => openParticipantDetail(entry, 'r')}
                    >
                      {fullNameR}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {isDetailPopupOpen && editableParticipant && (
          <EditParticipantPopup
            participant={
              editableParticipant[editableParticipant.editingParticipantKey]
            }
            identifier={editableParticipant.identifier}
            startupName={editableParticipant.startup_name}
            onClose={() => setIsDetailPopupOpen(false)}
            onSubmit={handleSubmitChanges}
            updateField={updateField}
            updateListField={updateListField}
            addListItem={addListItem}
            removeListItem={removeListItem}
            addStartupListItem={addStartupListItem}
            removeStartupListItem={removeStartupListItem}
            updateStartupListField={handleStartupListUpdate}
            onDelete={handleDeleteIdentifier} // <-- new prop
          />
        )}

        {isCreatePopupOpen && (
          <ManageStartupPageCreateStartupPopup
            onClose={() => setIsCreatePopupOpen(false)}
            onCreate={async (startupData) => {
              try {
                await client.post('/participant/create', startupData);
                setIsCreatePopupOpen(false);
                fetchParticipants(); // Refresh the list
              } catch (error) {
                console.error('Failed to create startup:', error);
                setBanner({
                  open: true,
                  message:
                    error.response && error.response.statusText
                      ? error.response.statusText
                      : 'Failed to create startup. Please try again.',
                  severity: 'error',
                });
              }
            }}
          />
        )}
        <Banner
          open={banner.open}
          message={banner.message}
          severity={banner.severity}
          onClose={() => setBanner({ ...banner, open: false })}
        />
        <ConfirmDialog
          open={deleteDialog.open}
          title="Delete Startup"
          content={
            <>
              Are you sure you want to delete the startup{' '}
              <b>{deleteDialog.identifier}</b>?<br />
              This action cannot be undone and is only possible if no surveys
              for that startup exist.
            </>
          }
          confirmLabel="Delete"
          cancelLabel="Cancel"
          onConfirm={confirmDeleteIdentifier}
          onCancel={cancelDeleteIdentifier}
        />
      </div>
    </MainLayout>
  );
};

export default ManageStartupPage;
